import React, { useState } from 'react';
import { Printer } from 'lucide-react';
import {
    ThermalConfig,
    BAUD_RATES,
    getThermalConfig,
    saveThermalConfig,
    DEFAULT_THERMAL,
    isUsbSupported,
    isSerialSupported,
    pickUsbPrinter,
    pickSerialPrinter
} from '../../services/ThermalPrinterService';
import { PaperWidthMm, readPaperWidth, savePaperWidth } from '../../utils/printerConfig';
import { printTestTicket } from '../../services/TicketPrintService';
import './ThermalPrinterSettings.css';

const WIDTHS: PaperWidthMm[] = [58, 80];

const describe = (config: ThermalConfig): string =>
    config.mode === 'usb' ? `USB · ${config.name}`
        : config.mode === 'serial' ? `Serie · ${config.name}`
            : 'Navegador (impresora predeterminada)';

/**
 * Impresora termica de tickets: se elige una vez, queda guardada en este
 * navegador y desde entonces el ticket sale directo a ella, sin ventana. La
 * factura (A4) no pasa por aqui: se imprime con el navegador.
 */
export function ThermalPrinterSettings() {
    const [config, setConfig] = useState<ThermalConfig>(() => getThermalConfig());
    const [width, setWidth] = useState<PaperWidthMm>(() => readPaperWidth());
    const [isBusy, setIsBusy] = useState(false);
    const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

    const usbOk = isUsbSupported();
    const serialOk = isSerialSupported();

    const update = (patch: Partial<ThermalConfig>) => {
        const next = { ...config, ...patch };
        setConfig(next);
        saveThermalConfig(next);
    };

    const pick = async (picker: () => Promise<Partial<ThermalConfig>>) => {
        if (isBusy) return;
        setIsBusy(true);
        setMessage(null);
        try {
            const chosen = await picker();
            update(chosen);
            setMessage({ text: `Impresora guardada: ${chosen.name}. Pulsa "Probar impresión".`, ok: true });
        } catch (err) {
            setMessage({ text: (err as any)?.message || 'No se pudo elegir la impresora.', ok: false });
        } finally {
            setIsBusy(false);
        }
    };

    const test = async () => {
        if (isBusy) return;
        setIsBusy(true);
        setMessage(null);
        try {
            await printTestTicket();
            setMessage({ text: 'Ticket de prueba enviado.', ok: true });
        } catch (err) {
            setMessage({ text: (err as any)?.message || 'No se pudo imprimir.', ok: false });
        } finally {
            setIsBusy(false);
        }
    };

    const reset = () => {
        setConfig(DEFAULT_THERMAL);
        saveThermalConfig(DEFAULT_THERMAL);
        setMessage({ text: 'Los tickets vuelven a salir por el navegador.', ok: true });
    };

    const chooseWidth = (next: PaperWidthMm) => {
        setWidth(next);
        savePaperWidth(next);
    };

    return (
        <div className="tp-card">
            <div className="tp-row">
                <div className="tp-title-group">
                    <div className="tp-icon"><Printer size={20} /></div>
                    <div>
                        <span className="tp-title">Impresora de Tickets</span>
                        <span className="tp-sub" data-testid="tp-current">{describe(config)}</span>
                    </div>
                </div>
                <div className="tp-widths" role="group" aria-label="Ancho del rollo">
                    {WIDTHS.map(w => (
                        <button key={w} type="button" onClick={() => chooseWidth(w)} className={`tp-width${width === w ? ' tp-width--active' : ''}`}>
                            {w} mm
                        </button>
                    ))}
                </div>
            </div>

            <div className="tp-actions">
                <button type="button" className="tp-btn" disabled={!usbOk || isBusy} onClick={() => pick(pickUsbPrinter)} data-testid="tp-usb">
                    Elegir impresora USB
                </button>
                <button type="button" className="tp-btn" disabled={!serialOk || isBusy} onClick={() => pick(pickSerialPrinter)} data-testid="tp-serial">
                    Elegir impresora serie
                </button>
                <button type="button" className="tp-btn tp-btn--dark" disabled={isBusy} onClick={test} data-testid="tp-test">
                    {isBusy ? 'Enviando…' : 'Probar impresión'}
                </button>
                {config.mode !== 'browser' && (
                    <button type="button" className="tp-btn tp-btn--muted" disabled={isBusy} onClick={reset} data-testid="tp-reset">
                        Quitar
                    </button>
                )}
            </div>

            {config.mode === 'serial' && (
                <label className="tp-field">
                    <span>Velocidad (baudios)</span>
                    <select value={config.baudRate} onChange={e => update({ baudRate: Number(e.target.value) })}>
                        {BAUD_RATES.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                </label>
            )}

            {config.mode !== 'browser' && (
                <label className="tp-check">
                    <input type="checkbox" checked={config.ascii} onChange={e => update({ ascii: e.target.checked })} />
                    <span>Texto sin acentos (si salen caracteres raros)</span>
                </label>
            )}

            {!usbOk && !serialOk && (
                <p className="tp-note">Este navegador no permite elegir impresora (solo Chrome y Edge). Los tickets salen por la impresora predeterminada.</p>
            )}
            <p className="tp-note">
                La impresora térmica debe ser ESC/POS. En Windows, una impresora USB necesita el controlador WinUSB para
                que el navegador pueda abrirla. La factura se imprime siempre con el navegador.
            </p>

            {message && <p className={message.ok ? 'tp-ok' : 'tp-error'} role="status">{message.text}</p>}
        </div>
    );
}
